import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CreditCard, QrCode, Loader2, CheckCircle2, Hourglass } from "lucide-react";

import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api, brl, dateBR, formatError } from "../lib/api";
import { PixInfo } from "../components/PixInfo";

export default function Pagamento() {
  const [paying, setPaying] = useState(false);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("pix");

  const load = useCallback(() => {
    api
      .get("/member/me")
      .then((r) => setMe(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const awaitingList = me?.payments.filter((p) => p.status === "awaiting") || [];
  const u = me?.user;

  async function doPay(method) {
    setPaying(true);
    try {
      await api.post("/member/payments/notify", { method });
      toast.success("Pagamento informado! Aguardando confirmação do pagamento.");
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setPaying(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-14" data-testid="payment-page">
      <p className="text-xs font-bold tracking-[0.2em] text-[#166534]">PAGAMENTO</p>
      <h1 className="font-display mt-2 text-3xl md:text-4xl font-extrabold tracking-tight">
        Mensalidade · 30 dias
      </h1>
      <p className="mt-2 text-neutral-700">
        Valor: <strong>{brl(30)}</strong> · Cada pagamento confirmado garante mais 30 dias. Quem paga
        antes do vencimento não perde nenhum dia.
      </p>
      {u?.last_payment_at && (
        <p className="mt-1 text-sm text-neutral-600" data-testid="payment-next-due">
          Próximo vencimento: <strong>{dateBR(u.next_due_date)}</strong>
        </p>
      )}

      {awaitingList.length > 0 && (
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="awaiting-confirmation">
          <Hourglass className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Aguardando confirmação do pagamento</p>
            <p>Informado em: {awaitingList.map((p) => dateBR(p.created_at)).join(", ")}</p>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="rounded-full bg-[#F0FDF4] p-1">
          <TabsTrigger
            value="pix"
            data-testid="tab-pix"
            className="rounded-full data-[state=active]:bg-[#166534] data-[state=active]:text-white px-5"
          >
            <QrCode className="w-4 h-4 mr-2" />
            Pix
          </TabsTrigger>
          <TabsTrigger
            value="card"
            data-testid="tab-card"
            className="rounded-full data-[state=active]:bg-[#166534] data-[state=active]:text-white px-5"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Cartão
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pix" className="mt-6">
          <div className="max-w-md">
            <PixInfo />
            <Button
              onClick={() => doPay("pix")}
              disabled={paying}
              data-testid="confirm-pix"
              className="mt-4 rounded-full bg-[#052E16] hover:bg-[#0a3a1f] hover:text-white text-white h-11 w-full"
            >
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Já fiz o Pix
                </>
              )}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="card" className="mt-6">
          <div className="max-w-md rounded-2xl border border-dashed border-neutral-300 bg-white p-6" data-testid="card-coming-soon">
            <CreditCard className="w-6 h-6 text-[#166534]" />
            <p className="font-display font-extrabold mt-3">Cartão de crédito e débito em breve</p>
            <p className="text-sm text-neutral-600 mt-1">
              Estamos preparando o pagamento por cartão por meio de uma plataforma segura. Por enquanto, utilize o Pix.
            </p>
            <Button variant="outline" className="mt-4 rounded-full" onClick={() => setTab("pix")} data-testid="card-use-pix">
              Pagar com Pix
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
